// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/ChatPhotos.tsx
// 🔧 PURPOSE: Le foto nella chat ospite: la griglia di miniature dentro la
//             bolla (ospite e staff), il visore a tutto schermo e la
//             striscia delle foto in partenza sopra la casella di testo.
// ============================================

import React, { useEffect, useState } from 'react';
import { AlertCircle, Check, ExternalLink, ImageIcon, ImageOff, Loader2, RotateCw, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ChatAttachment, CHAT_PHOTO_ONLY_TEXT, guestAssetUrl } from '../lib/guestApi';
import { useStatusBarVeil } from '../lib/statusBar';

/** Il messaggio e' solo "📷": il testo non si mostra, parlano le foto. */
export function isPhotoOnlyText(text: string | null | undefined): boolean {
  return (text || '').trim() === CHAT_PHOTO_ONLY_TEXT;
}

/** Foto cancellata dallo staff: resta nel messaggio senza url. */
export function isRemovedPhoto(att: ChatAttachment): boolean {
  return !att.url || !!att.deletedAt;
}

/**
 * Le miniature vere (Cloudinary) scaricate in anticipo: quando la bolla
 * passa dalle miniature locali a quelle del server non resta un buco.
 */
export function preloadChatThumbs(attachments: ChatAttachment[] | undefined): void {
  if (!Array.isArray(attachments) || typeof Image === 'undefined') return;
  attachments.forEach((att) => {
    if (!att || typeof att !== 'object' || isRemovedPhoto(att)) return;
    const url = guestAssetUrl(att.thumbUrl) || guestAssetUrl(att.url);
    if (url) {
      const img = new Image();
      img.src = url;
    }
  });
}

export interface ViewerPhoto {
  url: string;
  thumbUrl?: string;
}

/**
 * Un'immagine con dei ripieghi: se la prima sorgente non si carica (una
 * miniatura locale HEIC che il browser non sa leggere, un link rotto) si
 * passa alla successiva, e senza sorgenti resta un riquadro con l'icona.
 */
function Thumb({ srcs, className }: { srcs: string[]; className: string }) {
  const [failed, setFailed] = useState<string[]>([]);
  const src = srcs.find((s) => failed.indexOf(s) === -1);
  if (!src) {
    return (
      <span className={`${className} flex items-center justify-center bg-stone-200 dark:bg-stone-700`}>
        <ImageIcon className="h-6 w-6 text-stone-500" aria-hidden="true" />
      </span>
    );
  }
  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      decoding="async"
      draggable={false}
      onError={() => setFailed((prev) => (prev.indexOf(src) === -1 ? prev.concat(src) : prev))}
      className={className}
    />
  );
}

interface ChatPhotoGridProps {
  attachments: ChatAttachment[];
  /** Object URL delle foto appena scelte, allineati agli allegati: la bolla le mostra subito. */
  localThumbs?: string[];
  tone: 'guest' | 'staff';
  /** C'e' del testo sopra: un po' di spazio. */
  spaced?: boolean;
  /** Senza, le foto non si aprono (bolla non inviata: il tocco riprova l'invio). */
  onOpen?: (photo: ViewerPhoto) => void;
}

/**
 * Le foto di un messaggio: una sola larga al massimo 220px con le sue
 * proporzioni, due o piu' in una griglia di quadrati. Le proporzioni si
 * fissano prima che l'immagine arrivi (padding in percentuale, che va anche
 * dove aspect-ratio non c'e'): la bolla non cambia altezza a caricamento
 * finito e la lista non salta.
 */
export function ChatPhotoGrid({ attachments, localThumbs, tone, spaced, onOpen }: ChatPhotoGridProps) {
  const { t } = useTranslation();

  const photos: Array<{ key: string; srcs: string[]; full: string; thumb: string; ratio: number }> = [];
  let removed = 0;
  (Array.isArray(attachments) ? attachments : []).forEach((att, i) => {
    if (!att || typeof att !== 'object') return;
    if (att.kind && att.kind !== 'image') return;
    if (isRemovedPhoto(att)) {
      removed++;
      return;
    }
    const local = localThumbs && localThumbs[i] ? localThumbs[i] : '';
    const thumb = guestAssetUrl(att.thumbUrl) || '';
    const full = guestAssetUrl(att.url) || '';
    const srcs = [local, thumb, full].filter((s, j, arr) => !!s && arr.indexOf(s) === j);
    // Foto molto alte o molto larghe si tagliano: la bolla resta ragionevole.
    const ratio = att.width > 0 && att.height > 0
      ? Math.min(Math.max(att.width / att.height, 0.6), 1.9)
      : 4 / 3;
    photos.push({ key: `${i}-${att.publicId || ''}`, srcs, full, thumb, ratio });
  });

  if (photos.length === 0 && removed === 0) return null;

  const single = photos.length === 1;
  const imgClass = 'absolute inset-0 h-full w-full object-cover';

  const renderTile = (p: typeof photos[number]) => {
    const inner = (
      <>
        <span className="block" style={{ paddingBottom: `${single ? 100 / p.ratio : 100}%` }} />
        <Thumb srcs={p.srcs} className={imgClass} />
      </>
    );
    const tileClass = `relative block w-full overflow-hidden ${single ? 'rounded-xl' : 'rounded-lg'} ${
      tone === 'guest' ? 'bg-blue-500/40' : 'bg-stone-100 dark:bg-stone-800'
    }`;
    if (!onOpen || !p.full) {
      return (
        <div key={p.key} className={tileClass}>
          {inner}
        </div>
      );
    }
    return (
      <button
        key={p.key}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onOpen({ url: p.full, thumbUrl: p.thumb || undefined });
        }}
        aria-label={t('guestChat.photo.only')}
        className={`${tileClass} focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400`}
      >
        {inner}
      </button>
    );
  };

  return (
    <div className={spaced ? 'mt-1.5' : ''}>
      {photos.length > 0 && (
        <div className={`w-[220px] max-w-full ${single ? '' : 'grid grid-cols-2 gap-1'}`}>
          {photos.map(renderTile)}
        </div>
      )}
      {removed > 0 && (
        <span
          className={`inline-flex items-center gap-1 rounded-full bg-stone-200 dark:bg-stone-700 px-2 py-0.5 text-[12px] font-medium text-stone-700 dark:text-stone-300 ${
            photos.length > 0 ? 'mt-1' : ''
          }`}
        >
          <ImageOff className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
          {removed > 1 ? `${removed} × ` : ''}
          {t('guestChat.photo.removed')}
        </span>
      )}
    </div>
  );
}

interface ChatPhotoViewerProps {
  photo: ViewerPhoto | null;
  onClose: () => void;
}

/**
 * La foto a tutto schermo. Si chiude toccando ovunque (o con Esc); "Apri"
 * la porta nel browser, da dove l'ospite puo' salvarla o ingrandirla.
 * Va messo dentro il contenuto del drawer: fuori, il drawer lo prenderebbe
 * per un tocco "esterno" e si chiuderebbe.
 */
export function ChatPhotoViewer({ photo, onClose }: ChatPhotoViewerProps) {
  const { t } = useTranslation();
  const url = photo ? photo.url : '';
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setLoaded(false);
    setFailed(false);
  }, [url]);

  useEffect(() => {
    if (!url) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [url, onClose]);

  // Il fondo quasi nero della foto aperta, anche sotto la barra di stato (lib/statusBar).
  useStatusBarVeil(0.95, !!photo && !!url);

  if (!photo || !url) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('guestChat.photo.only')}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 px-2 pt-[calc(3.5rem+var(--nva-screen-top,0px))] pb-[calc(0.5rem+env(safe-area-inset-bottom))]"
      onClick={onClose}
    >
      {/* Mentre arriva la foto intera si vede la miniatura, gia' in cache. */}
      {!loaded && photo.thumbUrl && (
        <img src={photo.thumbUrl} alt="" aria-hidden="true" className="absolute max-h-[80%] max-w-[90%] object-contain opacity-60" />
      )}
      {!loaded && !failed && (
        <Loader2 className="absolute h-8 w-8 animate-spin text-white/80" aria-hidden="true" />
      )}
      {!failed && (
        <img
          src={url}
          alt={t('guestChat.photo.only')}
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
          draggable={false}
          className={`relative max-h-full max-w-full object-contain select-none transition-opacity ${loaded ? 'opacity-100' : 'opacity-0'}`}
        />
      )}
      <div className="absolute right-3 top-[calc(0.5rem+var(--nva-screen-top,0px))] flex items-center gap-2">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex h-11 items-center gap-1.5 rounded-full bg-white/15 px-3.5 text-[13px] font-bold text-white hover:bg-white/25 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          <ExternalLink className="h-4 w-4" aria-hidden="true" />
          {t('guestChat.photo.open')}
        </a>
        <button
          type="button"
          autoFocus
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          aria-label={t('common.close')}
          className="h-11 w-11 rounded-full bg-white/15 text-white flex items-center justify-center hover:bg-white/25 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

/**
 * Una foto scelta dall'ospite e non ancora spedita:
 *  - 'processing' = si sta comprimendo (niente anteprima ancora);
 *  - 'uploading'  = in caricamento, o in fila per caricarsi (una alla volta);
 *  - 'ready'      = caricata: partira' con il prossimo invio;
 *  - 'failed'     = caricamento fallito (tocca per riprovare) o foto
 *                   troppo grande / in un formato che il server non
 *                   accetta (retryable false: si puo' solo togliere).
 */
export type PendingPhotoState = 'processing' | 'uploading' | 'ready' | 'failed';

export interface PendingPhoto {
  id: string;
  /** Object URL dell'anteprima (la foto compressa, o l'originale HEIC). */
  previewUrl: string | null;
  state: PendingPhotoState;
  attachment?: ChatAttachment;
  retryable?: boolean;
  /** Perche' il caricamento e' fallito (codice del server, es. 'unsupported_format'). */
  errorCode?: string;
}

interface PendingPhotoStripProps {
  photos: PendingPhoto[];
  onRetry: (id: string) => void;
  onRemove: (id: string) => void;
}

function PendingTile({ photo, onRetry, onRemove }: { photo: PendingPhoto; onRetry: (id: string) => void; onRemove: (id: string) => void }) {
  const { t } = useTranslation();
  const [previewBroken, setPreviewBroken] = useState(false);
  const busy = photo.state === 'processing' || photo.state === 'uploading';
  const canRetry = photo.state === 'failed' && photo.retryable !== false;
  const label = busy
    ? t('guestChat.photo.uploading')
    : photo.state === 'failed'
    ? (canRetry ? t('guestChat.photo.failed') : t(photo.errorCode === 'unsupported_format' ? 'guestChat.photo.unsupported' : 'guestChat.photo.tooLarge'))
    : t('guestChat.photo.only');

  const content = (
    <>
      {photo.previewUrl && !previewBroken ? (
        <img
          src={photo.previewUrl}
          alt=""
          draggable={false}
          onError={() => setPreviewBroken(true)}
          className="h-full w-full object-cover"
        />
      ) : (
        <span className="flex h-full w-full items-center justify-center">
          <ImageIcon className="h-6 w-6 text-stone-500" aria-hidden="true" />
        </span>
      )}
      {busy && (
        <span className="absolute inset-0 flex items-center justify-center bg-black/40">
          <Loader2 className="h-5 w-5 animate-spin text-white" aria-hidden="true" />
        </span>
      )}
      {photo.state === 'failed' && (
        <span className="absolute inset-0 flex items-center justify-center bg-red-600/60 text-white">
          {canRetry ? <RotateCw className="h-5 w-5" aria-hidden="true" /> : <AlertCircle className="h-5 w-5" aria-hidden="true" />}
        </span>
      )}
      {photo.state === 'ready' && (
        <span className="absolute bottom-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-white">
          <Check className="h-3 w-3" aria-hidden="true" />
        </span>
      )}
    </>
  );

  const tileClass = 'relative block h-16 w-16 overflow-hidden rounded-xl bg-stone-200 dark:bg-stone-700';

  return (
    <div className="relative flex-shrink-0">
      {canRetry ? (
        <button type="button" onClick={() => onRetry(photo.id)} aria-label={label} title={label} className={tileClass}>
          {content}
        </button>
      ) : (
        <div role="img" aria-label={label} title={label} className={tileClass}>
          {content}
        </div>
      )}
      <button
        type="button"
        onClick={() => onRemove(photo.id)}
        aria-label={t('guestChat.photo.remove')}
        title={t('guestChat.photo.remove')}
        className="absolute -right-1.5 -top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-stone-800/90 text-white ring-2 ring-nva-card dark:ring-stone-900 before:absolute before:-inset-2.5 before:content-['']"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/** Le foto in partenza, sopra la casella: anteprima, stato e una X per toglierle. */
export function PendingPhotoStrip({ photos, onRetry, onRemove }: PendingPhotoStripProps) {
  if (photos.length === 0) return null;
  return (
    // pt/pr: la X sporge dall'angolo e lo scorrimento orizzontale la taglierebbe.
    // -mx-4/px-4: arriva ai bordi della barra in basso (padding 16px).
    <div className="scrollbar-hide -mx-4 flex gap-2.5 overflow-x-auto px-4 pb-1 pr-5 pt-2">
      {photos.map((p) => (
        <PendingTile key={p.id} photo={p} onRetry={onRetry} onRemove={onRemove} />
      ))}
    </div>
  );
}
